import { call, put, takeEvery, select, take } from "redux-saga/effects";
import { APPEARANCE_ACTION_TYPES } from "../../constants/enrolledAppearanceConstants";
import {
  fetchAppearancesSuccess,
  fetchAppearancesFailure,
  deleteAppearanceSuccess,
  deleteAppearanceFailure,
  createAppearanceSuccess,
  createAppearanceFailure,
  resetCreateSuccess,
  fetchAssociatedStreamsSuccess,
  fetchAssociatedStreamsFailure,
  uploadAppearanceImageSuccess,
  uploadAppearanceImageFailure,
  deleteAppearanceImageSuccess,
  deleteAppearanceImageFailure,
  downloadAppearanceImagesSuccess,
  downloadAppearanceImagesFailure,
  enrollAppearanceSuccess,
  enrollAppearanceFailure,
  submitAppearanceSuccess,
  fetchAppearancesRequest,
  retryFailedImageFailure,
} from "../actions/ea-actions";
import {
  getAppearances,
  deleteAppearance,
  createAppearance,
  updateAppearance,
  submitAppearance,
  uploadAppearanceImage,
  deleteAppearanceImage,
  downloadAllAppearanceImages,
  enrollAppearance,
} from "../../api/EnrolledAppearanceServiceManager";
import { fetchImageUrls } from "../../pages/AssociatedStreams/services/services";

import { showNotificationBanner } from "../actions";
import { APPEARANCE_TEXT, APPEARANCE_RUNTIME_STATUS } from "../../constants/enrolledAppearanceConstants";
import { notificationTypes } from "../../layouts/NotificationConfig";

// Selector to get active session
const activeSession = (state) => state.sessionReducer.activeSession;

function* validateActiveSession() {
  const session = yield select(activeSession);
  if (!session?.activeSession) {
    throw new Error('No active session found');
  }
  return session.activeSession;
 }

// Fetch Appearances Saga
function* fetchAppearancesSaga(action) {
  try {
    const session = yield call(validateActiveSession);
    const response = yield call(
      getAppearances,
      action.payload,
      session
    );
    yield put(fetchAppearancesSuccess({ response, appearanceId: action.payload?.resourceidentifier?.id}));
  } catch (error) {
    yield put(
      fetchAppearancesFailure(
        (error?.errorResponse || error?.response)?.data?.error_description || error?.message || "Failed to fetch appearances"
      )
    );
  }
}

// Delete Appearance Saga
function* deleteAppearanceSaga(action) {
  try {
    const session = yield call(validateActiveSession);
    yield call(deleteAppearance, action.payload, session);
    yield put(deleteAppearanceSuccess(action.payload));
    // Refetch appearances list after successful delete
    yield put(fetchAppearancesRequest({}));
  } catch (error) {
    yield put(
      deleteAppearanceFailure(
        (error?.errorResponse || error?.response)?.data?.error_description || error?.message || "Failed to delete appearance"
      )
    );
  }
}

// Create Appearance Saga
function* createAppearanceSaga(action) {
  try {
    const session = yield call(validateActiveSession)
    const response = yield call(
      createAppearance,
      action.payload,
      session
    );
    yield put(createAppearanceSuccess(response.data || response));
  } catch (error) {
    const errorMsg = (error?.errorResponse || error?.response)?.data?.error_description || error?.message || "Failed to create appearance";
    yield put(createAppearanceFailure(errorMsg));
    yield put(showNotificationBanner({
      type: notificationTypes[115],
      subtitle: errorMsg,
      showNotification: true,
      priority: "critical",
      isDismissible: true,
    }));
  }
}

// Fetch Associated Streams Saga
function* fetchAssociatedStreamsSaga(action) {
  try {
    const { roiList } = action.payload;

    // ROI card images are now fetched live per-card via SnapshotComponent,
    // so this only needs to group/transform roiList into the site/camera structure.
    const transformedData = yield call(fetchImageUrls, roiList);

    yield put(fetchAssociatedStreamsSuccess(transformedData));
  } catch (error) {
    yield put(
      fetchAssociatedStreamsFailure(
        (error?.errorResponse || error?.response)?.data?.error_description || error?.message || "Failed to fetch associated streams"
      )
    );
  }
}
// Download Appearance Images Saga
function* downloadAppearanceImagesSaga(action) {
  try {
    const { accountName, appearanceId, images } = action.payload;
    const session = yield call(validateActiveSession);

    const downloadedImages = yield call(
      downloadAllAppearanceImages,
      appearanceId,
      images,
      session
    );

    yield put(downloadAppearanceImagesSuccess(appearanceId, downloadedImages));
  } catch (error) {
    yield put(
      downloadAppearanceImagesFailure(
        action.payload.appearanceId,
        (error?.errorResponse || error?.response)?.data?.error_description || error?.message || "Unable to download images. Please try again."
      )
    );
  }
}

// Upload Appearance Image Saga
function* uploadAppearanceImageSaga(action) {
  try {
    const session = yield call(validateActiveSession);
    const { fileId, appearanceId, file, imageUrl } = action.payload;
    const response = yield call(uploadAppearanceImage, { appearanceId, file }, session);
    yield put(
      uploadAppearanceImageSuccess({
        data: response,
        fileId,
        imageUrl
      })
    );
  } catch (error) {
    const { fileId } = action.payload;
    const errorMsg = (error?.errorResponse || error?.response)?.data?.error_description || error?.message || "Unable to upload image. Please try again.";
    yield put(uploadAppearanceImageFailure(fileId, errorMsg));
    yield put(showNotificationBanner({
      type: notificationTypes[114],
      subtitle: errorMsg,
      showNotification: true,
      priority: "critical",
      isDismissible: true,
    }));
  }
}

// Delete Appearance Image Saga
function* deleteAppearanceImageSaga(action) {
  try {
    const session = yield call(validateActiveSession);
    const { imageId, appearanceId, shouldRefreshList = false } = action.payload;
    yield call(deleteAppearanceImage,  imageId, appearanceId, session);
    yield put(deleteAppearanceImageSuccess(imageId, appearanceId));
    if (shouldRefreshList) {
      yield put(fetchAppearancesRequest({}));
    }
    yield put(showNotificationBanner({
      type: notificationTypes[112],
      showNotification: true,
      priority: "success",
      isDismissible: true,
    }));
  } catch (error) {
    const { imageId } = action.payload;
    const errorMsg = (error?.errorResponse || error?.response)?.data?.error_description || error?.message || "Unable to delete image. Please try again.";
    yield put(deleteAppearanceImageFailure(imageId, errorMsg));
    yield put(showNotificationBanner({
      type: notificationTypes[113],
      subtitle: errorMsg,
      showNotification: true,
      priority: "critical",
      isDismissible: true,
    }));
  }
}

// Enroll Appearance Saga
function* enrollAppearanceSaga(action) {
  try {
    const session = yield call(validateActiveSession);
    const { appearanceId, sitesToAdd, sitesToRemove, appearanceStatus, enrollOnly } = action.payload;

    const hasSiteDelta = (sitesToAdd?.length ?? 0) > 0 || (sitesToRemove?.length ?? 0) > 0;
    const needsSubmit = !enrollOnly && appearanceStatus === APPEARANCE_RUNTIME_STATUS.VALID;

    let enrollResponse = null;
    if (hasSiteDelta) {
      enrollResponse = yield call(enrollAppearance, { appearanceId, sitesToAdd, sitesToRemove }, session);
    }

    if (needsSubmit) {
      const submitResponse = yield call(submitAppearance, { appearanceId }, session);
      if (submitResponse?.id) {
        yield put(submitAppearanceSuccess(submitResponse));
      }
    }

    const enrolledSiteIds = enrollResponse?.enrolledSiteIds ?? null;
    const responseAppearanceId = enrollResponse?.id ?? appearanceId;
    yield put(enrollAppearanceSuccess({ appearanceId: responseAppearanceId, enrolledSiteIds }));
    const addCount = sitesToAdd?.length ?? 0;
    const removeCount = sitesToRemove?.length ?? 0;
    const siteWord = (n) => `${n} ${n !== 1 ? APPEARANCE_TEXT.SITE_PLURAL : APPEARANCE_TEXT.SITE_SINGULAR}`;
    let enrollSubTitle;
    if (addCount > 0 && removeCount > 0) {
      enrollSubTitle = `${APPEARANCE_TEXT.ENROLLED_TO_PREFIX} ${siteWord(addCount)} ${APPEARANCE_TEXT.ENROLLED_AND_UNENROLLED_JOIN} ${siteWord(removeCount)}`;
    } else if (addCount > 0) {
      enrollSubTitle = `${APPEARANCE_TEXT.ENROLLED_TO_PREFIX} ${siteWord(addCount)}`;
    } else if (removeCount > 0) {
      enrollSubTitle = `${APPEARANCE_TEXT.UNENROLLED_FROM_PREFIX} ${siteWord(removeCount)}`;
    }
    yield put(showNotificationBanner({
      type: 'Appearance deployed successfully',
      data: {
        title: APPEARANCE_TEXT.SUCCESSFULLY_DEPLOYED_TITLE,
        subTitle: enrollSubTitle,
      },
      showNotification: true,
      priority: 'success',
      isDismissible: true,
    }));
  } catch (error) {
    const errorMsg = (error?.errorResponse || error?.response)?.data?.error_description || error?.message || APPEARANCE_TEXT.DEPLOY_TO_COMPANION_ERROR;
    yield put(enrollAppearanceFailure(errorMsg));
    yield put(showNotificationBanner({
      type: APPEARANCE_TEXT.DEPLOY_TO_COMPANION_ERROR,
      data: {
        title: APPEARANCE_TEXT.DEPLOY_TO_COMPANION_ERROR,
        subTitle: errorMsg,
      },
      showNotification: true,
      priority: 'critical',
      isDismissible: true,
    }));
  }
}

// Retry Failed Image Saga — delete stale failed image then re-upload from cache blob URL
function* retryFailedImageSaga(action) {
  const { imageId, appearanceId, blobUrl, imageName } = action.payload;

  if (!blobUrl) {
    yield put(retryFailedImageFailure());
    yield put(showNotificationBanner({
      type: notificationTypes[116],
      showNotification: true,
      priority: "critical",
      isDismissible: true,
    }));
    return;
  }

  try {
    const session = yield call(validateActiveSession);

    // Capture pre-retry FAILED imageIds (excluding the one being retried) to detect new failures after upload
    const allAppearances = yield select((state) => state.enrolledAppearanceReducer.appearances);
    const preRetryAppearance = Array.isArray(allAppearances)
      ? allAppearances.find((a) => a.id === appearanceId)
      : null;
    const preRetryFailedIds = new Set(
      (preRetryAppearance?.images || [])
        .filter((img) => img.imageId !== imageId && img.status === APPEARANCE_RUNTIME_STATUS.FAILED)
        .map((img) => img.imageId)
    );

    yield call(deleteAppearanceImage, imageId, appearanceId, session);

    const response = yield call(fetch, blobUrl);
    const blob = yield call([response, response.blob]);
    const file = new File([blob], imageName || `image-${imageId}.jpg`, { type: blob.type });

    yield call(uploadAppearanceImage, { appearanceId, file }, session);
    yield put(fetchAppearancesRequest({}));

    // New image gets a new imageId — check for any FAILED image not present before retry
    const fetchResult = yield take(APPEARANCE_ACTION_TYPES.FETCH_APPEARANCES_SUCCESS);
    const targetAppearance = fetchResult.payload.response?.find((a) => a.id === appearanceId);
    const hasNewFailedImage = (targetAppearance?.images || []).some(
      (img) => img.status === APPEARANCE_RUNTIME_STATUS.FAILED && !preRetryFailedIds.has(img.imageId)
    );
    if (hasNewFailedImage) {
      yield put(showNotificationBanner({
        type: notificationTypes[116],
        subtitle: APPEARANCE_TEXT.RETRY_VALIDATE_ERROR,
        showNotification: true,
        priority: "critical",
        isDismissible: true,
      }));
    }
  } catch (error) {
    const errorMsg = (error?.errorResponse || error?.response)?.data?.error_description || error?.message || APPEARANCE_TEXT.RETRY_VALIDATE_ERROR;
    yield put(retryFailedImageFailure());
    yield put(showNotificationBanner({
      type: notificationTypes[116],
      subtitle: errorMsg,
      showNotification: true,
      priority: "critical",
      isDismissible: true,
    }));
  }
}

// Watcher Saga
export function* enrolledAppearanceSaga() {
  yield takeEvery(
    APPEARANCE_ACTION_TYPES.FETCH_APPEARANCES_REQUEST,
    fetchAppearancesSaga
  );
  yield takeEvery(
    APPEARANCE_ACTION_TYPES.DELETE_APPEARANCE_REQUEST,
    deleteAppearanceSaga
  );
  yield takeEvery(
    APPEARANCE_ACTION_TYPES.CREATE_APPEARANCE_REQUEST,
    createAppearanceSaga
  );
  yield takeEvery(
    APPEARANCE_ACTION_TYPES.FETCH_ASSOCIATED_STREAMS_REQUEST,
    fetchAssociatedStreamsSaga
  );
  yield takeEvery(
    APPEARANCE_ACTION_TYPES.UPLOAD_APPEARANCE_IMAGE_REQUEST,
    uploadAppearanceImageSaga
  );
  yield takeEvery(
    APPEARANCE_ACTION_TYPES.DELETE_APPEARANCE_IMAGE_REQUEST,
    deleteAppearanceImageSaga
  );
  yield takeEvery(
    APPEARANCE_ACTION_TYPES.DOWNLOAD_APPEARANCE_IMAGES_REQUEST,
    downloadAppearanceImagesSaga
  );
  yield takeEvery(
    APPEARANCE_ACTION_TYPES.ENROLL_APPEARANCE_REQUEST,
    enrollAppearanceSaga
  );
  yield takeEvery(
    APPEARANCE_ACTION_TYPES.RETRY_FAILED_IMAGE_REQUEST,
    retryFailedImageSaga
  );
};


export default enrolledAppearanceSaga;
